import { IndexedDBFactory, IndexedDBConnectionError } from '../../../utils/IndexedDBManager';
import { BufferedEventBus } from '../../../utils/BufferedEventBus';
import { MixPanelEventEmitter } from '../../../metrics/MixPanelEventEmitter';
import { Logger } from '../../../support/Logger';
import { IRecording } from '../RecordingState';
import { RECORDING_ERROR_EVENT } from '../RecordingService';
import { IndexedDBRecordingProvider } from './IndexedDBRecordingProvider';
import { FilesystemRecordingProvider } from './FilesystemRecordingProvider';
import { DevSettings } from '../../../support/DevSettings';
import { isPlatform } from '../../../utils/isPlatform';

type StorageBackend = 'indexeddb' | 'filesystem';
type StorageOperation = 'add' | 'get' | 'getAll' | 'update' | 'delete';

/**
 * Redundant recording storage with automatic fallback.
 *
 * Fallback chain:
 * 1. Try IndexedDB (primary storage)
 * 2. If IndexedDB fails, try Filesystem (native platforms only)
 * 3. If both fail, emit error event for user notification
 *
 * This ensures recordings are never lost due to storage failures.
 */
export class RedundantRecordingStorage {
    static inject = () => [
        IndexedDBFactory,
        FilesystemRecordingProvider,
        BufferedEventBus,
        MixPanelEventEmitter,
        Logger,
        DevSettings
    ];

    private indexedDBProvider: IndexedDBRecordingProvider;
    private filesystemProvider: FilesystemRecordingProvider;

    constructor(
        indexedDBFactory: IndexedDBFactory<IRecording>,
        filesystemProvider: FilesystemRecordingProvider,
        private events: BufferedEventBus,
        private mixpanel: MixPanelEventEmitter,
        private logger: Logger,
        private _devSettings: DevSettings
    ) {
        const indexedDB = indexedDBFactory.get('innerhive', 'recordings');
        this.indexedDBProvider = new IndexedDBRecordingProvider(indexedDB);
        this.filesystemProvider = filesystemProvider;
    }

    async add(data: IRecording): Promise<string> {
        return this.executeWithFallback({
            operation: 'add',
            recordingId: data.id,
            primary: () => this.indexedDBProvider.add(data),
            fallback: () => this.filesystemProvider.add(data)
        });
    }

    async get(id: string): Promise<IRecording | undefined> {
        // For get, try IndexedDB first, then Filesystem (no error if both miss)
        try {
            const result = await this.indexedDBProvider.get(id);
            if (result) {
                return result;
            }
        } catch (error) {
            this.logger.info('IndexedDB get failed, trying filesystem', { id, error });
        }

        // Try filesystem as fallback
        try {
            const filesystemAvailable = await this.filesystemProvider.isAvailable();
            if (filesystemAvailable) {
                return await this.filesystemProvider.get(id);
            }
        } catch (error) {
            this.logger.info('Filesystem get failed', { id, error });
        }

        return undefined;
    }

    async getAll(): Promise<IRecording[]> {
        const recordingsMap = new Map<string, IRecording>();

        // Get from IndexedDB
        try {
            const indexedDBRecordings = await this.indexedDBProvider.getAll();
            for (const recording of indexedDBRecordings) {
                recordingsMap.set(recording.id, recording);
            }
        } catch (error) {
            this.logger.info('IndexedDB getAll failed', { error });
        }

        // Get from Filesystem and merge (IndexedDB takes precedence)
        try {
            const filesystemAvailable = await this.filesystemProvider.isAvailable();
            if (filesystemAvailable) {
                const filesystemRecordings = await this.filesystemProvider.getAll();
                for (const recording of filesystemRecordings) {
                    // Only add if not already in IndexedDB
                    if (!recordingsMap.has(recording.id)) {
                        recordingsMap.set(recording.id, recording);
                    }
                }
            }
        } catch (error) {
            this.logger.info('Filesystem getAll failed', { error });
        }

        return Array.from(recordingsMap.values());
    }

    async update(data: IRecording): Promise<void> {
        // Try to find where the record exists and update there
        let updatedInIndexedDB = false;
        let updatedInFilesystem = false;

        // Try IndexedDB first
        try {
            const existsInIndexedDB = await this.indexedDBProvider.get(data.id);
            if (existsInIndexedDB) {
                await this.indexedDBProvider.update(data);
                updatedInIndexedDB = true;
            }
        } catch (error) {
            this.logger.info('IndexedDB update failed', { id: data.id, error });
        }

        // Try Filesystem
        try {
            const filesystemAvailable = await this.filesystemProvider.isAvailable();
            if (filesystemAvailable) {
                const existsInFilesystem = await this.filesystemProvider.get(data.id);
                if (existsInFilesystem) {
                    await this.filesystemProvider.update(data);
                    updatedInFilesystem = true;
                }
            }
        } catch (error) {
            this.logger.info('Filesystem update failed', { id: data.id, error });
        }

        // If not found in either, use fallback logic to add
        if (!updatedInIndexedDB && !updatedInFilesystem) {
            await this.executeWithFallback({
                operation: 'update',
                recordingId: data.id,
                primary: () => this.indexedDBProvider.add(data).then(() => {}),
                fallback: () => this.filesystemProvider.add(data).then(() => {})
            });
        }
    }

    async delete(id: string): Promise<void> {
        // Delete from both stores to ensure cleanup
        const errors: Error[] = [];

        try {
            await this.indexedDBProvider.delete(id);
        } catch (error) {
            errors.push(error as Error);
        }

        try {
            const filesystemAvailable = await this.filesystemProvider.isAvailable();
            if (filesystemAvailable) {
                await this.filesystemProvider.delete(id);
            }
        } catch (error) {
            errors.push(error as Error);
        }

        // Only throw if both failed
        if (errors.length === 2) {
            this.logger.error('Failed to delete from both storage backends', { id, errors });
        }
    }

    /**
     * Check if any storage backend is available for recording.
     * Used for pre-recording check to ensure we can save recordings.
     * Throws IndexedDBConnectionError if no storage is available.
     */
    async checkStorageConnection(): Promise<void> {
        // Check if IndexedDB is available
        const indexedDBAvailable = await this.indexedDBProvider.isAvailable();
        if (indexedDBAvailable) {
            return; // Primary storage is working
        }

        // IndexedDB failed, check if filesystem fallback is available
        const filesystemAvailable = await this.filesystemProvider.isAvailable();
        if (filesystemAvailable) {
            return; // Fallback storage is working
        }

        // Neither storage is available - throw error for pre-recording check to handle
        throw new IndexedDBConnectionError('No storage backend available');
    }

    private async executeWithFallback<T>(params: {
        operation: StorageOperation;
        recordingId: string;
        primary: () => Promise<T>;
        fallback: () => Promise<T>;
    }): Promise<T> {
        const { operation, recordingId, primary, fallback } = params;

        // Try IndexedDB first
        try {
            const result = await primary();
            this.trackStorageOperation({
                operation,
                backend: 'indexeddb',
                recordingId,
                wasFallback: false
            });
            return result;
        } catch (indexedDBError) {
            this.logger.info(`IndexedDB ${operation} failed, trying filesystem fallback`, {
                recordingId,
                error: indexedDBError
            });

            // Try Filesystem fallback
            try {
                const filesystemAvailable = await this.filesystemProvider.isAvailable();
                if (!filesystemAvailable) {
                    throw new Error('Filesystem storage not available');
                }

                const result = await fallback();
                this.trackStorageOperation({
                    operation,
                    backend: 'filesystem',
                    recordingId,
                    wasFallback: true
                });

                // Track successful filesystem fallback for visibility in MixPanel
                this.mixpanel.track('Recording Saved to Filesystem Fallback', () => ({
                    operation,
                    'Recording Id': recordingId
                }));

                return result;
            } catch (filesystemError) {
                // Both failed - emit error event
                this.logger.error('Both storage backends failed', {
                    operation,
                    recordingId,
                    indexedDBError,
                    filesystemError
                });

                this.trackStorageFailure({ operation, recordingId });
                this.emitStorageError(recordingId);

                // Re-throw the original error
                throw indexedDBError;
            }
        }
    }

    private trackStorageOperation(params: {
        operation: StorageOperation;
        backend: StorageBackend;
        recordingId: string;
        wasFallback: boolean;
    }): void {
        const { operation, backend, recordingId, wasFallback } = params;

        // Only track significant operations (add) to avoid noise
        if (operation === 'add') {
            this.mixpanel.track('Recording Storage Operation', () => ({
                operation,
                backend,
                'Recording Id': recordingId,
                'Was Fallback': wasFallback
            }));
        }
    }

    private trackStorageFailure(params: {
        operation: StorageOperation;
        recordingId: string;
    }): void {
        this.mixpanel.track('Recording Storage Failure', () => ({
            operation: params.operation,
            'Recording Id': params.recordingId
        }));
    }

    private emitStorageError(recordingId: string): void {
        const isWeb = !isPlatform('capacitor');
        const message = isWeb
            ? 'Innerhive is having issues saving recordings. Please try closing this tab and opening a new one. If that doesn\'t work, quit your browser completely and reopen it. If the issue persists, try logging out and back in, or contact us for help.'
            : 'Innerhive is having issues saving to your device, please try closing and restarting the app. If the issue persists, try deleting and reinstalling the app, or contact us for help.';

        this.events.emit(RECORDING_ERROR_EVENT, {
            recordingId,
            errorType: 'storage-failed',
            title: 'Quick Fix Needed',
            message
        });
    }
}
